import COS from 'cos-js-sdk-v5';
import { injectable, inject } from 'inversify';
import { getCosSession } from '../../api/cos';
import { UploadParams, CosBucketMap, UploadResult } from './typings';
import { getRandomStr } from '../../../util/crypto';
import { IBuildConfigService } from '../../../typings/service/build-config';
import { IUserService } from '../../../typings/service/user-service';
import { logger } from '../../../util/logger';

export { Bucket } from './typings';

@injectable()
export class UploaderService {
  private readonly appId = '1300342587';
  private readonly region = 'ap-shanghai';
  private readonly cosBucketMap: CosBucketMap = {
    user: { test: 'knock-user-test', prod: 'knock-user' },
    im: { test: 'knock-im-test', prod: 'knock-im' },
    log: { test: 'knock-log-test', prod: 'knock-log' },
    group: { test: 'knock-group-test', prod: 'knock-group' },
  };

  private cos: COS;

  constructor(
    @inject('BuildConfigService') private buildConfigService: IBuildConfigService,
    @inject('UserService') private userService: IUserService,
  ) {
    this.cos = new COS({
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      getAuthorization: this.getCosAuthorization.bind(this),
      UploadAddMetaMd5: true,
    });
  }


  private get env() {
    return this.buildConfigService.config.env;
  }


  public async upload(params: UploadParams): Promise<UploadResult> {
    const ext = params.file.name.slice(params.file.name.lastIndexOf('.'));
    const bucketName = this.cosBucketMap[params.bucket][this.env];
    const bucket = `${bucketName}-${this.appId}`;
    const prefix = this.userService.getUploadPrefix();
    const id = getRandomStr(12);
    const name = `${prefix}_${id}${ext}`;
    const key = `${params.path}/${name}`;
    const res = await this.cosUpload({
      Body: params.file,
      Bucket: bucket,
      Region: this.region,
      Key: key,
      onProgress: params.onProgress,
    });
    const location = res.Location;
    return {
      url: `https://${location}`,
      etag: res.ETag.replace(/['"]/g, ''), // cos返回的字符串里面有引号
      size: params.file.size,
    };
  }


  private async cosUpload(params: COS.PutObjectParams): Promise<COS.PutObjectResult> {
    try {
      return await this.cos.putObject(params);
    } catch (error) {
      const e: COS.CosError = error as COS.CosError;
      logger.error(`cosUpload error: #${e?.code} ${e?.message}`);
      throw error;
    }
  }

  private getCosAuthorization(
    options: COS.GetAuthorizationOptions,
    callback: (params: COS.GetAuthorizationCallbackParams) => void,
  ) {
    this.getCosSessionConfig(options.Bucket, options.Region).then((credentials) => {
      callback({
        TmpSecretId: credentials.tmpSecretId,
        TmpSecretKey: credentials.tmpSecretKey,
        SecurityToken: credentials.sessionToken,
        StartTime: credentials.beginTime,
        ExpiredTime: credentials.expiredTime,
        ScopeLimit: true,
      });
    })
      .catch(() => {
      // 这里必须传入callback触发回调错误
        callback({
          TmpSecretId: '',
          TmpSecretKey: '',
          SecurityToken: '',
          StartTime: 0,
          ExpiredTime: 0,
          ScopeLimit: true,
        });
      });
  }

  private async getCosSessionConfig(bucket: string, region: string) {
    const bucketName = bucket.slice(0, bucket.lastIndexOf('-'));
    const appid = bucket.slice(bucket.lastIndexOf('-') + 1);
    const res = await getCosSession(bucketName, region, appid);
    return {
      sessionToken: res.sessionToken,
      // 临时密钥 secretKey
      tmpSecretId: res.tmpSecretId,
      // // 临时密钥 Token
      tmpSecretKey: res.tmpSecretKey,
      // // 临时密钥有效截止时间戳，单位是秒
      beginTime: parseInt(res.beginTime, 10),
      // // 临时密钥有效截止时间戳，单位是秒
      expiredTime: parseInt(res.expiredTime, 10),
      // // 最后一次更新密钥的时间戳，单位毫秒
      lastTime: Date.now(),
    };
  }
}
